/** Experimental projection of a verified current-user evaluation conversation. */
import { z } from "zod";
import type { AuthProvider } from "../auth/types.js";
import { RESOURCES } from "../auth/types.js";
import type { DataverseApi } from "./dataverse-api.js";
import {
  ACTIVITY_ENVIRONMENT_PATTERN,
  ACTIVITY_GUID_PATTERN,
  type CopilotActivityApi,
} from "./copilot-activity-api.js";
import { environmentIdToApiHost } from "./connections-api.js";
import { requestWithRetry } from "../utils/http.js";
import { ApiError } from "../utils/errors.js";
import { projectCopilotMcpCatalogStructure } from "./copilot-mcp-catalog-structure.js";
import { projectCopilotRecordedToolSchema } from "./copilot-recorded-tool-schema.js";

const DEADLINE_MS = 20000;
const MAX_JSON_BYTES = 1024 * 1024;
const MAX_ACTIVITIES = 200;
const MAX_BOT_MESSAGES = 5;
const MAX_BOT_MESSAGE_CHARS = 2000;
const MAX_BOT_TOTAL_CHARS = 4000;
const MAX_TOOL_RESULTS = 5;
const MAX_TOOL_RESULT_JSON_CHARS = 16384;
const MAX_TOOL_SUMMARY_CHARS = 1000;
const MAX_TOOL_STATUS_CHARS = 200;
const MAX_TOOL_TOTAL_CHARS = 4000;
const MAX_TOOL_COMMENTS = 3;
const MAX_TOOL_COMMENT_CHARS = 600;
const MAX_TOOL_VISIBILITY_CHARS = 120;
const MAX_TOOL_COMMENT_PAGE_ITEMS = 200;
const MAX_SHAPE_KEYS = 12;
const MAX_SHAPE_DEPTH = 3;
export const TOOL_RESULT_SITE_PATTERN =
  /^https:\/\/[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.atlassian\.net$(?![\s\S])/;
export const TOOL_RESULT_ISSUE_PATTERN = /^(?:HELP|HR)-[1-9][0-9]{0,9}$(?![\s\S])/;
export const TOOL_RESULT_SERVER_PATTERN =
  /^[A-Za-z_][A-Za-z0-9_]{0,199}\.(?:topic|action)\.[A-Za-z_][A-Za-z0-9_]{0,199}$(?![\s\S])/;
const THREAD_PATTERN = /^19:[A-Za-z0-9_-]+@thread\.v2$(?![\s\S])/;
const SCHEMA_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,199}$(?![\s\S])/;
const CHANNEL = "pva-maker-evaluation";
const EVENTS = [
  "DynamicServerInitialize",
  "DynamicServerInitializeConfirmation",
  "DynamicServerToolsList",
  "DynamicPlanReceived",
  "DynamicPlanReceivedDebug",
  "DynamicPlanStepTriggered",
  "DynamicPlanStepBindUpdate",
  "DialogTracing",
  "startConversation",
] as const;
const PARAMETERS = ["cloudId", "issueIdOrKey", "fields", "expand", "properties"] as const;
const parameterSet = new Set<string>(PARAMETERS);
const eventSet = new Set<string>(EVENTS);

export const getCopilotAgentActivityInputSchema = z
  .object({
    environment: z.string().regex(ACTIVITY_ENVIRONMENT_PATTERN),
    agentId: z.string().regex(ACTIVITY_GUID_PATTERN),
    conversationId: z.string().regex(ACTIVITY_GUID_PATTERN),
    includeBotMessages: z.boolean().optional(),
    includeToolCatalogStructure: z.boolean().optional(),
    includeRecordedToolSchema: z.boolean().optional(),
    toolCatalogTarget: z
      .object({
        serverSchemaName: z.string().regex(TOOL_RESULT_SERVER_PATTERN),
        toolName: z.literal("getJiraIssue"),
      })
      .strict()
      .optional(),
    includeToolResults: z.boolean().optional(),
    toolResultTarget: z
      .object({
        cloudId: z.string().regex(TOOL_RESULT_SITE_PATTERN),
        issueIdOrKey: z.string().regex(TOOL_RESULT_ISSUE_PATTERN),
        serverSchemaName: z.string().regex(TOOL_RESULT_SERVER_PATTERN),
        fieldSelection: z.enum(["summary-status", "status-only", "comment-only"]).optional(),
      })
      .strict()
      .optional(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.includeRecordedToolSchema === true && value.includeToolCatalogStructure !== true)
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Recorded tool metadata requires the explicit catalog structure opt-in and exact target.",
      });
    if ((value.includeToolCatalogStructure === true) !== (value.toolCatalogTarget !== undefined))
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Catalog structure requires an explicit opt-in and exact comparison target together.",
      });
    if ((value.includeToolResults === true) !== (value.toolResultTarget !== undefined))
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Tool-result content requires an explicit opt-in and exact target together.",
      });
  });
export type CopilotActivityDetailInput = z.infer<typeof getCopilotAgentActivityInputSchema>;
type Failure =
  | "input"
  | "unavailable"
  | "environment"
  | "identity"
  | "list"
  | "target"
  | "agent"
  | "authentication"
  | "http"
  | "response"
  | "deadline";
type Stage = "thread lookup" | "identity metadata" | "history";
const FAILURE_LABELS: Record<Failure, string> = {
  input: "invalid exact target",
  unavailable: "required existing API capability unavailable",
  environment: "explicit environment must match the unchanged current Dataverse context",
  identity: "selected account or returned conversation ownership could not be verified",
  list: "current-user first-page ownership preflight unavailable",
  target:
    "exact conversation was not uniquely present among the first 50 projected own-user rows on the supported evaluation channel",
  agent: "exact agent schema preflight unavailable or mismatched",
  authentication: "existing silent Power Platform authentication unavailable",
  http: "fixed history read was refused",
  response: "history response is malformed or exceeds bounds",
  deadline: "history read deadline exceeded",
};
export class CopilotActivityDetailError extends ApiError {
  constructor(reason: Failure, statusCode?: number, stage?: Stage) {
    const status =
      Number.isInteger(statusCode) && statusCode! >= 100 && statusCode! <= 599 ? statusCode! : 502;
    const stageLabel =
      stage === "thread lookup" || stage === "identity metadata" || stage === "history"
        ? `; ${stage}`
        : "";
    super(
      `Copilot activity detail: ${FAILURE_LABELS[reason]}${reason === "http" ? ` (HTTP ${status}${stageLabel})` : ""}. No history metadata returned; no retry, alternate route, sign-in or tenant change was attempted.`,
      status,
    );
    this.name = "CopilotActivityDetailError";
  }
}
function object(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
function canonicalEnvironment(value: string) {
  return /^default-/i.test(value) ? `Default-${value.slice(8).toLowerCase()}` : value.toLowerCase();
}
function timestamp(value: unknown): string | null {
  // Studio history uses both UTC spellings; do not accept arbitrary timezone offsets.
  if (typeof value !== "string" || value.length > 33) return null;
  const match =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,7})?(?:Z|\+00:00)$(?![\s\S])/.exec(
      value,
    );
  if (!match) return null;
  const [year, month, day, hour, minute, second] = match.slice(1).map(Number) as [
    number,
    number,
    number,
    number,
    number,
    number,
  ];
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return year > 0 &&
    month >= 1 &&
    month <= 12 &&
    day > 0 &&
    day <= days[month - 1]! &&
    hour < 24 &&
    minute < 60 &&
    second < 60 &&
    Number.isFinite(Date.parse(value))
    ? value
    : null;
}
function state(value: unknown) {
  return value === undefined ? "absent" : value === null ? "null" : "unsupported";
}
function argumentKeys(value: unknown) {
  if (!object(value))
    return {
      state: state(value),
      parameterNames: [],
      parameterCount: null,
      unknownParameterCount: null,
    };
  const keys = Object.keys(value);
  if (keys.length > 50)
    return {
      state: "unsupported",
      parameterNames: [],
      parameterCount: null,
      unknownParameterCount: null,
    };
  return {
    state: "available",
    parameterNames: PARAMETERS.filter((name) => keys.includes(name)),
    parameterCount: keys.length,
    unknownParameterCount: keys.filter((name) => !parameterSet.has(name)).length,
  };
}
function autoFilledNames(value: unknown) {
  if (!Array.isArray(value) || value.length > 50)
    return {
      state: state(value),
      parameterNames: [],
      entryCount: null,
      unknownParameterCount: null,
      unsupportedEntryCount: null,
    };
  const unsupportedEntryCount = value.filter((item) => typeof item !== "string").length;
  return {
    state: unsupportedEntryCount ? "unsupported" : "available",
    parameterNames: PARAMETERS.filter((name) => value.includes(name)),
    entryCount: value.length,
    unknownParameterCount: value.filter(
      (item) => typeof item !== "string" || !parameterSet.has(item),
    ).length,
    unsupportedEntryCount,
  };
}

/**
 * Conversation text is untrusted sensitive content, even from the bot. This is
 * conservative, best-effort redaction, not a guarantee that text contains no PII
 * or secrets. Inspect the full bounded string before truncating its output.
 */
function redactBotMessage(value: string): { text: string | null; redacted: boolean } {
  // Normalize the complete string before testing too: removing a control later
  // must not turn an undetected split credential label into a visible secret.
  const normalized = value.replace(
    // eslint-disable-next-line no-control-regex
    /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2066-\u2069\ufeff]/g,
    "",
  );
  const hasCredentialCue = (candidate: string) =>
    /\b(?:bearer|authorization|cookies?|passwords?|passwds?|secrets?|tokens?|(?:access|refresh|id)[ _-]?tokens?|api[ _-]?keys?|client[ _-]?(?:secrets?|assertions?)|credentials?|auth[ _-]?codes?|session[ _-]?ids?|private[ _-]?keys?|nonces?)\b/i.test(
      candidate,
    ) ||
    /\bbasic\s+[A-Za-z0-9+/=_-]+/i.test(candidate) ||
    /\beyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/.test(candidate) ||
    /[A-Za-z0-9_+/-]{64,}/.test(candidate) ||
    /\b(?:sig|state)\b["']?\s*[:=]/i.test(candidate);
  const credentialBearing = hasCredentialCue(value) || hasCredentialCue(normalized);
  if (credentialBearing) return { text: null, redacted: true };
  const text = normalized
    .replace(/(?:[A-Za-z][A-Za-z0-9+.-]{0,31}:\/\/|\/\/|www\.)[^\s<>"']+/gi, "[URL OMITTED]")
    .replace(
      /\b[A-Za-z][A-Za-z0-9+.-]{0,31}:[^\s<>"']+/g,
      (match: string, offset: number, source: string) => {
        // A markdown label ending in closing emphasis has no URI payload.
        // Keep this exception exact; x:**payload** still contains a payload.
        const next = source[offset + match.length];
        return /^[A-Za-z][A-Za-z0-9+.-]{0,31}:[*_]{1,3}$/.test(match) &&
          (next === undefined || /\s/.test(next))
          ? match
          : "[URL OMITTED]";
      },
    );
  return { text, redacted: text !== value };
}

function projectBotMessages(rows: Record<string, unknown>[]) {
  const eligible = rows
    .map((row, activityIndex) => ({ row, activityIndex }))
    .filter(({ row }) => row.type === "message" && object(row.from) && row.from.role === "bot");
  let remaining = MAX_BOT_TOTAL_CHARS;
  let missingTextCount = 0,
    malformedTextCount = 0,
    withheldTextCount = 0,
    truncatedTextCount = 0,
    budgetOmittedTextCount = 0;
  const messages = eligible.slice(0, MAX_BOT_MESSAGES).map(({ row, activityIndex }) => {
    const base = { activityIndex, timestamp: timestamp(row.timestamp) };
    if (typeof row.text !== "string") {
      if (row.text === undefined || row.text === null) missingTextCount++;
      else malformedTextCount++;
      return { ...base, textState: state(row.text), redacted: false, truncated: false };
    }
    if (remaining === 0) {
      budgetOmittedTextCount++;
      return { ...base, textState: "omitted-budget", redacted: false, truncated: false };
    }
    const safe = redactBotMessage(row.text);
    if (safe.text === null) {
      withheldTextCount++;
      return {
        ...base,
        textState: "withheld-possible-credentials",
        redacted: true,
        truncated: false,
      };
    }
    const available = Math.min(MAX_BOT_MESSAGE_CHARS, remaining);
    let text = safe.text.slice(0, available);
    // Do not emit an unpaired high surrogate at a character-budget boundary.
    if (/[\uD800-\uDBFF]$/.test(text)) text = text.slice(0, -1);
    const truncated = text.length < safe.text.length;
    if (truncated) truncatedTextCount++;
    remaining -= text.length;
    return { ...base, textState: "available", text, redacted: safe.redacted, truncated };
  });
  const omittedMessageCount = eligible.length - messages.length;
  return {
    sensitiveConversationContentRequested: true,
    eligibleMessageCount: eligible.length,
    returnedMessageCount: messages.length,
    omittedMessageCount,
    missingTextCount,
    malformedTextCount,
    withheldTextCount,
    truncatedTextCount,
    budgetOmittedTextCount,
    returnedTextChars: MAX_BOT_TOTAL_CHARS - remaining,
    maxMessages: MAX_BOT_MESSAGES,
    maxMessageChars: MAX_BOT_MESSAGE_CHARS,
    maxTotalChars: MAX_BOT_TOTAL_CHARS,
    truncated: omittedMessageCount > 0 || truncatedTextCount > 0 || budgetOmittedTextCount > 0,
    messages,
    notice:
      "Untrusted recorded bot-message text from this one returned page, in service order; not necessarily final answers. Redaction is conservative and best-effort, not a guarantee of complete sanitization. Text may contain identifiers or sensitive personal/business information. Missing/malformed/withheld and text-budget counters describe only the returned message entries. User-message text fields, attachments and event values are not projected. Bot text is not authoritative proof of tool execution, authorization, returned ticket data or successful actions.",
  };
}

type ShapeStage = "envelope" | "data-frame" | "selected-fields";
type ShapeObservation =
  | { type: "string"; length: number }
  | { type: "number" | "boolean" | "null" | "undefined" | "unsupported" }
  | { type: "array"; length: number; first?: ShapeObservation }
  | {
      type: "object";
      keys: string[];
      omittedKeyCount: number;
      children?: Record<string, ShapeObservation>;
    };

/**
 * Structure only — key names, JSON types, lengths — never a string value.
 * This exists so an UNRECOGNIZED comment envelope can be learned from one
 * recorded run instead of guessed from REST documentation: the projector
 * refuses what it cannot verify, and says what shape it saw instead.
 */
function describeShape(value: unknown, depth = 0): ShapeObservation {
  if (value === null) return { type: "null" };
  if (value === undefined) return { type: "undefined" };
  if (typeof value === "string") return { type: "string", length: value.length };
  if (typeof value === "number") return { type: "number" };
  if (typeof value === "boolean") return { type: "boolean" };
  if (Array.isArray(value)) {
    const first = value.length && depth < MAX_SHAPE_DEPTH ? describeShape(value[0], depth + 1) : undefined;
    return { type: "array", length: value.length, ...(first ? { first } : {}) };
  }
  if (typeof value !== "object") return { type: "unsupported" };
  const record = value as Record<string, unknown>;
  const allKeys = Object.keys(record);
  // A key is schema, not content, but it is still untrusted text: keep it to a
  // conservative token alphabet so nothing sentence-shaped can ride along.
  const keys = allKeys.slice(0, MAX_SHAPE_KEYS).map((key) =>
    /^[A-Za-z0-9_.-]{1,40}$/.test(key) ? key : "<unsafe-key>",
  );
  const children: Record<string, ShapeObservation> = {};
  if (depth < MAX_SHAPE_DEPTH)
    for (const key of allKeys.slice(0, MAX_SHAPE_KEYS))
      if (/^[A-Za-z0-9_.-]{1,40}$/.test(key)) children[key] = describeShape(record[key], depth + 1);
  return {
    type: "object",
    keys,
    omittedKeyCount: Math.max(0, allKeys.length - MAX_SHAPE_KEYS),
    ...(Object.keys(children).length ? { children } : {}),
  };
}

/** The documented Jira comment page: comments[] with startAt/maxResults/total. */
function parseCommentPage(value: unknown):
  | { comments: Record<string, unknown>[]; startAt: number; maxResults: number; total: number }
  | null {
  const isCount = (candidate: unknown): candidate is number =>
    typeof candidate === "number" && Number.isSafeInteger(candidate) && candidate >= 0;
  if (!object(value)) return null;
  if (!Object.keys(value).every((key) => ["comments", "startAt", "maxResults", "total"].includes(key)))
    return null;
  if (
    !Array.isArray(value.comments) ||
    value.comments.length > MAX_TOOL_COMMENT_PAGE_ITEMS ||
    !value.comments.every((item) => object(item)) ||
    !isCount(value.startAt) ||
    !isCount(value.maxResults) ||
    !isCount(value.total)
  )
    return null;
  return {
    comments: value.comments,
    startAt: value.startAt,
    maxResults: value.maxResults,
    total: value.total,
  };
}

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:?\d{2})$(?![\s\S])/;

/** Only this observed getJiraIssue read-result shape is supported; no planner internals. */
function projectToolResults(
  rows: Record<string, unknown>[],
  target: NonNullable<CopilotActivityDetailInput["toolResultTarget"]>,
) {
  const taskDialogId = `MCP:${target.serverSchemaName}:getJiraIssue`;
  // This selects the evidence contract, never changes or widens a ticket call.
  const statusOnly = target.fieldSelection === "status-only";
  const commentOnly = target.fieldSelection === "comment-only";
  const selectedFields = commentOnly ? ["comment"] : statusOnly ? ["status"] : ["summary", "status"];
  const isBotEvent = (row: Record<string, unknown>, name: string) =>
    row.type === "event" && row.name === name && object(row.from) && row.from.role === "bot";
  const candidates = rows
    .map((row, activityIndex) => ({ row, activityIndex }))
    .filter(({ row }) => row.type === "event" && row.name === "DynamicPlanStepFinished");
  const onlyKeys = (value: Record<string, unknown>, allowed: string[]) =>
    Object.keys(value).every((key) => allowed.includes(key));
  const emptyOptional = (value: unknown) =>
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && !value.length);
  type ResultState =
    | "recorded-tool-result"
    | "unsupported-event"
    | "unsupported-action"
    | "unverified-binding"
    | "unverified-requested-inputs"
    | "not-completed"
    | "recorded-tool-error"
    | "unsupported-observation"
    | "over-inspection-limit"
    | "unsupported-result-shape"
    | "unverified-result-target"
    | "withheld-possible-credentials"
    | "omitted-text-budget";
  const stateCounts: Record<ResultState, number> = {
    "recorded-tool-result": 0,
    "unsupported-event": 0,
    "unsupported-action": 0,
    "unverified-binding": 0,
    "unverified-requested-inputs": 0,
    "not-completed": 0,
    "recorded-tool-error": 0,
    "unsupported-observation": 0,
    "over-inspection-limit": 0,
    "unsupported-result-shape": 0,
    "unverified-result-target": 0,
    "withheld-possible-credentials": 0,
    "omitted-text-budget": 0,
  };
  let remaining = MAX_TOOL_TOTAL_CHARS;
  let redactedFieldCount = 0,
    truncatedFieldCount = 0;
  const results = candidates.slice(0, MAX_TOOL_RESULTS).map(({ row, activityIndex }) => {
    const base = { activityIndex, timestamp: timestamp(row.timestamp) };
    const withheld = (
      resultState: ResultState,
      shape?: { observedAt: ShapeStage; observation: ShapeObservation },
    ) => {
      stateCounts[resultState]++;
      // Structure is disclosed only for the comment contract, whose live
      // envelope is unverified; the established contracts stay silent. The
      // stage says WHICH container was inspected — three sites share one
      // shape vocabulary, and "an empty object" alone cannot be located.
      return {
        ...base,
        resultState,
        ...(commentOnly && shape
          ? { shapeObservation: { observedAt: shape.observedAt, ...shape.observation } }
          : {}),
      };
    };
    const projectCommentPage = (page: NonNullable<ReturnType<typeof parseCommentPage>>) => {
      // The service's returned order is kept; Jira documents ascending creation
      // order for this field, so the tail is the newest RETURNED — not proof of
      // the newest overall when the page is partial.
      const tail = page.comments.slice(-MAX_TOOL_COMMENTS);
      let withheldCommentCount = 0;
      const boundedText = (safe: { text: string; redacted: boolean }, maxChars: number) => {
        let text = safe.text.slice(0, Math.min(maxChars, remaining));
        if (/[\uD800-\uDBFF]$/.test(text)) text = text.slice(0, -1);
        const truncated = text.length < safe.text.length;
        if (truncated) truncatedFieldCount++;
        if (safe.redacted) redactedFieldCount++;
        remaining -= text.length;
        return { text, redacted: safe.redacted, truncated };
      };
      const items = tail.map((comment) => {
        const id =
          typeof comment.id === "string" && /^[1-9][0-9]{0,19}$(?![\s\S])/.test(comment.id)
            ? comment.id
            : null;
        const instant = (candidate: unknown) =>
          typeof candidate === "string" && ISO_INSTANT.test(candidate) ? candidate : null;
        const created = instant(comment.created);
        const updated = instant(comment.updated);
        let body:
          | { bodyState: "text"; text: string; redacted: boolean; truncated: boolean }
          | { bodyState: "absent" | "withheld-possible-credentials" | "omitted-text-budget" }
          | { bodyState: "unsupported-body-shape"; bodyShape: ShapeObservation };
        if (comment.body === undefined) body = { bodyState: "absent" };
        else if (typeof comment.body !== "string")
          body = { bodyState: "unsupported-body-shape", bodyShape: describeShape(comment.body) };
        else {
          const safe = redactBotMessage(comment.body);
          if (safe.text === null) {
            withheldCommentCount++;
            body = { bodyState: "withheld-possible-credentials" };
          } else if (!remaining && safe.text.length) body = { bodyState: "omitted-text-budget" };
          else
            body = {
              bodyState: "text",
              ...boundedText({ text: safe.text, redacted: safe.redacted }, MAX_TOOL_COMMENT_CHARS),
            };
        }
        let visibility:
          | { visibilityState: "absent" | "unsupported" | "withheld-possible-credentials" }
          | {
              visibilityState: "present";
              type: { text: string; redacted: boolean; truncated: boolean };
              value: { text: string; redacted: boolean; truncated: boolean };
            };
        if (comment.visibility === undefined) visibility = { visibilityState: "absent" };
        else if (
          !object(comment.visibility) ||
          typeof comment.visibility.type !== "string" ||
          typeof comment.visibility.value !== "string"
        )
          visibility = { visibilityState: "unsupported" };
        else {
          const type = redactBotMessage(comment.visibility.type);
          const value = redactBotMessage(comment.visibility.value);
          visibility =
            type.text === null || value.text === null
              ? { visibilityState: "withheld-possible-credentials" }
              : {
                  visibilityState: "present",
                  type: boundedText({ text: type.text, redacted: type.redacted }, MAX_TOOL_VISIBILITY_CHARS),
                  value: boundedText(
                    { text: value.text, redacted: value.redacted },
                    MAX_TOOL_VISIBILITY_CHARS,
                  ),
                };
        }
        // author, updateAuthor, self, avatar URLs, renderedBody, jsdPublic and
        // properties are never projected, whatever the record carries.
        return {
          ...(id === null ? { idState: "absent-or-unsupported" as const } : { id }),
          ...(created === null ? { createdState: "absent-or-unsupported" as const } : { created }),
          ...(updated === null ? {} : { updated }),
          ...body,
          ...visibility,
        };
      });
      stateCounts["recorded-tool-result"]++;
      return {
        ...base,
        resultState: "recorded-tool-result" as const,
        recordedToolResult: {
          toolName: "getJiraIssue",
          recordedState: "completed",
          recordedIsError: false,
          exactRecordedBindingMatched: true,
          requestedFields: ["comment"],
          sourceOrigin: target.cloudId,
          issue: {
            id: data.id as string,
            key: target.issueIdOrKey,
            comments: {
              page: {
                startAt: page.startAt,
                maxResults: page.maxResults,
                total: page.total,
                returnedCount: page.comments.length,
                pageCoverageComplete:
                  page.startAt === 0 && page.startAt + page.comments.length >= page.total,
              },
              selection: "last-returned-in-service-order",
              projectedCount: items.length,
              omittedCommentCount: page.comments.length - items.length,
              withheldCommentCount,
              maxComments: MAX_TOOL_COMMENTS,
              maxCommentChars: MAX_TOOL_COMMENT_CHARS,
              items,
            },
          },
        },
      };
    };
    if (!isBotEvent(row, "DynamicPlanStepFinished") || !object(row.value))
      return withheld("unsupported-event");
    const value = row.value;
    if (value.taskDialogId !== taskDialogId) return withheld("unsupported-action");
    // stepId is opaque. Compare it only; never expose it or infer a schema from it.
    if (typeof value.stepId !== "string" || !value.stepId.length || value.stepId.length > 256)
      return withheld("unverified-binding");
    const sameStep = (candidate: Record<string, unknown>) =>
      object(candidate.value) &&
      candidate.value.taskDialogId === taskDialogId &&
      candidate.value.stepId === value.stepId;
    const bindings = rows
      .map((candidate, index) => ({ candidate, index }))
      .filter(
        ({ candidate }) =>
          candidate.type === "event" &&
          candidate.name === "DynamicPlanStepBindUpdate" &&
          sameStep(candidate),
      );
    if (
      bindings.length !== 1 ||
      bindings[0]!.index >= activityIndex ||
      !isBotEvent(bindings[0]!.candidate, "DynamicPlanStepBindUpdate") ||
      candidates.filter(({ row: candidate }) => sameStep(candidate)).length !== 1
    )
      return withheld("unverified-binding");
    const args = (bindings[0]!.candidate.value as Record<string, unknown>).arguments;
    const actualFields = object(args) ? args.fields : undefined;
    if (
      !object(args) ||
      !onlyKeys(args, ["cloudId", "issueIdOrKey", "fields", "expand", "properties"]) ||
      args.cloudId !== target.cloudId ||
      args.issueIdOrKey !== target.issueIdOrKey ||
      !Array.isArray(actualFields) ||
      actualFields.length !== selectedFields.length ||
      !selectedFields.every((field) => actualFields.includes(field)) ||
      !emptyOptional(args.expand) ||
      !emptyOptional(args.properties)
    )
      return withheld("unverified-requested-inputs");
    if (value.state !== "completed") return withheld("not-completed");
    if (!object(value.observation)) return withheld("unsupported-observation");
    const observation = value.observation;
    if (observation.isError === true) return withheld("recorded-tool-error");
    if (
      observation.isError !== false ||
      !Array.isArray(observation.content) ||
      observation.content.length !== 1 ||
      !object(observation.content[0]) ||
      observation.content[0].type !== "text" ||
      typeof observation.content[0].text !== "string"
    )
      return withheld("unsupported-observation");
    let text = observation.content[0].text;
    if (text.length > MAX_TOOL_RESULT_JSON_CHARS) return withheld("over-inspection-limit");
    if (text.startsWith("[CIT:")) {
      const prefix =
        /^\[CIT:[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}\] /.exec(
          text,
        );
      if (!prefix) return withheld("unsupported-result-shape");
      text = text.slice(prefix[0].length);
    }
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return withheld("unsupported-result-shape");
    }
    if (!object(parsed) || !onlyKeys(parsed, ["data"]) || !object(parsed.data))
      return withheld("unsupported-result-shape", {
        observedAt: "envelope",
        observation: describeShape(parsed),
      });
    const data = parsed.data;
    const frameOk =
      onlyKeys(data, ["appliedContentFormat", "id", "key", "fields"]) &&
      data.appliedContentFormat === "markdown" &&
      typeof data.id === "string" &&
      /^[1-9][0-9]{0,19}$(?![\s\S])/.test(data.id) &&
      object(data.fields) &&
      onlyKeys(data.fields, selectedFields);
    if (commentOnly) {
      if (!frameOk)
        return withheld("unsupported-result-shape", {
          observedAt: "data-frame",
          observation: describeShape(data),
        });
      if (data.key !== target.issueIdOrKey) return withheld("unverified-result-target");
      const fields = data.fields as Record<string, unknown>;
      const page = parseCommentPage(fields.comment);
      if (!page)
        return withheld("unsupported-result-shape", {
          observedAt: "selected-fields",
          observation: describeShape(fields),
        });
      return projectCommentPage(page);
    }
    // frameOk proved data.fields is an object holding only the selected keys.
    const fields = frameOk ? (data.fields as Record<string, unknown>) : undefined;
    const statusField = fields && object(fields.status) ? fields.status : undefined;
    if (
      !fields ||
      (!statusOnly && typeof fields.summary !== "string") ||
      !statusField ||
      !onlyKeys(statusField, ["name"]) ||
      typeof statusField.name !== "string"
    )
      return withheld("unsupported-result-shape");
    if (data.key !== target.issueIdOrKey) return withheld("unverified-result-target");
    // Inspect only the explicitly selected fields. A status-only observation
    // containing a summary or other field was already refused above.
    const summary = statusOnly ? null : redactBotMessage(fields.summary as string);
    const status = redactBotMessage(statusField.name);
    if (summary?.text === null || status.text === null)
      return withheld("withheld-possible-credentials");
    if (!remaining && (summary?.text.length || status.text.length))
      return withheld("omitted-text-budget");
    const boundedField = (safe: { text: string; redacted: boolean }, maxChars: number) => {
      let text = safe.text.slice(0, Math.min(maxChars, remaining));
      if (/[\uD800-\uDBFF]$/.test(text)) text = text.slice(0, -1);
      const truncated = text.length < safe.text.length;
      if (truncated) truncatedFieldCount++;
      if (safe.redacted) redactedFieldCount++;
      remaining -= text.length;
      return { text, redacted: safe.redacted, truncated };
    };
    stateCounts["recorded-tool-result"]++;
    return {
      ...base,
      resultState: "recorded-tool-result" as const,
      recordedToolResult: {
        toolName: "getJiraIssue",
        recordedState: "completed",
        recordedIsError: false,
        exactRecordedBindingMatched: true,
        requestedFields: [...selectedFields],
        sourceOrigin: target.cloudId,
        issue: {
          id: data.id,
          key: target.issueIdOrKey,
          ...(summary === null
            ? {}
            : {
                summary: boundedField(
                  { text: summary.text, redacted: summary.redacted },
                  MAX_TOOL_SUMMARY_CHARS,
                ),
              }),
          status: boundedField(
            { text: status.text, redacted: status.redacted },
            MAX_TOOL_STATUS_CHARS,
          ),
        },
      },
    };
  });
  const omittedEventCount = candidates.length - results.length;
  return {
    sensitiveToolResultContentRequested: true,
    source: "recorded-tool-result",
    candidateEventCount: candidates.length,
    returnedEventCount: results.length,
    omittedEventCount,
    stateCounts,
    redactedFieldCount,
    truncatedFieldCount,
    returnedTextChars: MAX_TOOL_TOTAL_CHARS - remaining,
    maxResults: MAX_TOOL_RESULTS,
    maxResultJsonChars: MAX_TOOL_RESULT_JSON_CHARS,
    maxSummaryChars: MAX_TOOL_SUMMARY_CHARS,
    maxStatusChars: MAX_TOOL_STATUS_CHARS,
    maxTotalTextChars: MAX_TOOL_TOTAL_CHARS,
    truncated:
      omittedEventCount > 0 || truncatedFieldCount > 0 || stateCounts["omitted-text-budget"] > 0,
    results,
    notice:
      "Recorded getJiraIssue evidence only, not a fresh read, current ticket-state verification, effective-grant check or action-write proof. Only exact target-correlated bot finish events with one prior matching bind event, completed state, isError=false and the supported minimal result shape expose selected ticket fields. comment-only projects at most the last three RETURNED comments in service order — id, created/updated instants, bounded redacted body text and visibility presence — and never authors, avatars, self links, rendered bodies or properties; page counters are the service's and a projected tail is not proof of the newest comment overall, nor is visibility (or its absence) proof of effective audience. When a comment-only result has an unrecognized shape, only key names, JSON types and lengths are disclosed, never values. This first page may omit events; only the first five candidate finish events in service order are inspected and state counters describe those entries. Summary/status are untrusted sensitive business content with best-effort redaction, not guaranteed complete sanitization; redaction/truncation may alter displayed values. No raw observation, citation IDs, opaque step/task identifiers, other tool results, planner/debug internals or arbitrary error messages are projected.",
  };
}

export class CopilotActivityDetailApi {
  constructor(
    private readonly authProvider: AuthProvider,
    private readonly dataverseApi: Pick<DataverseApi, "getCopilotAgentIdentitySilent">,
    private readonly activityApi: Pick<CopilotActivityApi, "listActivity">,
    private readonly currentEnvironmentId: () => string,
  ) {}

  async getActivity(input: CopilotActivityDetailInput) {
    const parsed = getCopilotAgentActivityInputSchema.safeParse(input);
    if (!parsed.success) throw new CopilotActivityDetailError("input");
    const environment = canonicalEnvironment(parsed.data.environment);
    const agentId = parsed.data.agentId.toLowerCase(),
      conversationId = parsed.data.conversationId.toLowerCase();
    const controller = new AbortController();
    let body: Awaited<ReturnType<typeof requestWithRetry>>["body"] | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const destroy = () => {
      try {
        body?.destroy();
      } catch {
        /* Cleanup is never diagnostic content. */
      }
    };
    const active = () => {
      if (controller.signal.aborted) {
        destroy();
        throw new CopilotActivityDetailError("deadline");
      }
    };
    const sameEnvironment = () => {
      const current = this.currentEnvironmentId();
      if (
        typeof current !== "string" ||
        !ACTIVITY_ENVIRONMENT_PATTERN.test(current) ||
        canonicalEnvironment(current) !== environment
      )
        throw new CopilotActivityDetailError("environment");
    };
    const work = async () => {
      sameEnvironment();
      if (
        !this.authProvider.tryGetTokenForResourceSilent ||
        !this.dataverseApi.getCopilotAgentIdentitySilent ||
        !this.activityApi.listActivity
      )
        throw new CopilotActivityDetailError("unavailable");
      const before = this.authProvider.getCurrentAccount();
      if (
        !before ||
        typeof before.username !== "string" ||
        !before.username.trim() ||
        typeof before.tenantId !== "string" ||
        !ACTIVITY_GUID_PATTERN.test(before.tenantId) ||
        typeof before.localAccountId !== "string" ||
        !ACTIVITY_GUID_PATTERN.test(before.localAccountId)
      )
        throw new CopilotActivityDetailError("identity");
      const selected = {
        username: before.username,
        tenantId: before.tenantId,
        objectId: before.localAccountId,
      };
      const check = () => {
        active();
        sameEnvironment();
        const now = this.authProvider.getCurrentAccount();
        if (
          !now ||
          now.username !== selected.username ||
          now.tenantId !== selected.tenantId ||
          now.localAccountId !== selected.objectId
        )
          throw new CopilotActivityDetailError("identity");
      };
      let listing: Awaited<ReturnType<CopilotActivityApi["listActivity"]>>;
      try {
        listing = await this.activityApi.listActivity({ environment, agentId, limit: 50 });
      } catch {
        active();
        throw new CopilotActivityDetailError("list");
      }
      check();
      if (
        listing.environment !== environment ||
        listing.agentId !== agentId ||
        listing.ownerMatchedCurrentUser !== true ||
        listing.allReturnedRowsIdentityMatched !== true ||
        !Array.isArray(listing.activities) ||
        listing.activities.length > 50
      )
        throw new CopilotActivityDetailError("identity");
      const matches = listing.activities.filter((row) => row.conversationId === conversationId);
      if (matches.length !== 1 || matches[0]!.channelId !== CHANNEL)
        throw new CopilotActivityDetailError("target");
      let bot: unknown;
      try {
        bot = await this.dataverseApi.getCopilotAgentIdentitySilent(agentId, {
          signal: controller.signal,
        });
      } catch {
        active();
        throw new CopilotActivityDetailError("agent");
      }
      check();
      if (
        !object(bot) ||
        typeof bot.botid !== "string" ||
        !ACTIVITY_GUID_PATTERN.test(bot.botid) ||
        bot.botid.toLowerCase() !== agentId ||
        typeof bot.schemaname !== "string" ||
        !SCHEMA_PATTERN.test(bot.schemaname)
      )
        throw new CopilotActivityDetailError("agent");
      const schema = bot.schemaname;
      if (
        parsed.data.toolCatalogTarget &&
        !parsed.data.toolCatalogTarget.serverSchemaName.startsWith(`${schema}.`)
      )
        throw new CopilotActivityDetailError("input");
      if (
        parsed.data.toolResultTarget &&
        !parsed.data.toolResultTarget.serverSchemaName.startsWith(`${schema}.`)
      )
        throw new CopilotActivityDetailError("input");
      let token: unknown;
      try {
        token = await this.authProvider.tryGetTokenForResourceSilent(RESOURCES.POWER_PLATFORM_API);
      } catch {
        active();
        throw new CopilotActivityDetailError("authentication");
      }
      check();
      if (typeof token !== "string" || !token.trim() || /\s/.test(token))
        throw new CopilotActivityDetailError("authentication");
      const origin = `https://${environmentIdToApiHost(environment)}`;
      const read = async (path: string, stage: Stage, plainText = false): Promise<unknown> => {
        check();
        try {
          const response = await requestWithRetry(
            `${origin}${path}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: plainText ? "text/plain" : "application/json",
              },
              signal: controller.signal,
              headersTimeout: 10000,
              bodyTimeout: 10000,
            },
            { maxAttempts: 1 },
          );
          body = response.body;
          check();
          if (response.statusCode !== 200)
            throw new CopilotActivityDetailError("http", response.statusCode, stage);
          const contentType = response.headers["content-type"];
          if (
            typeof contentType !== "string" ||
            !(
              plainText
                ? /^text\/plain(?:\s*;|$)/i
                : /^application\/(?:json|[a-z0-9.+-]+\+json)(?:\s*;|$)/i
            ).test(contentType)
          )
            throw new CopilotActivityDetailError("response");
          const chunks: Buffer[] = [];
          let size = 0;
          for await (const chunk of body as AsyncIterable<Uint8Array>) {
            check();
            const bytes = Buffer.from(chunk);
            size += bytes.length;
            if (size > (plainText ? 1024 : MAX_JSON_BYTES))
              throw new CopilotActivityDetailError("response");
            chunks.push(bytes);
          }
          check();
          const text = Buffer.concat(chunks).toString("utf8");
          return plainText ? text : JSON.parse(text);
        } finally {
          destroy();
          body = undefined;
        }
      };
      const thread = await read(
        `/powervirtualagents/bots/${schema}/channels/${CHANNEL}/conversations/${conversationId}/history/threadId?api-version=1`,
        "thread lookup",
        true,
      );
      check();
      if (typeof thread !== "string" || thread.length > 256 || !THREAD_PATTERN.test(thread))
        throw new CopilotActivityDetailError("response");
      const threadSegment = encodeURIComponent(thread);
      const query = new URLSearchParams({
        "api-version": "1",
        botSchemaName: schema,
        channelId: "unknown",
      });
      const metadata = await read(
        `/powervirtualagents/conversation/${threadSegment}/metadata?${query.toString()}`,
        "identity metadata",
      );
      check();
      if (
        !object(metadata) ||
        metadata.id !== thread ||
        typeof metadata.conversationId !== "string" ||
        !ACTIVITY_GUID_PATTERN.test(metadata.conversationId) ||
        metadata.conversationId.toLowerCase() !== conversationId ||
        typeof metadata.userId !== "string" ||
        metadata.userId.toLowerCase() !== `8:orgid:${selected.objectId.toLowerCase()}` ||
        metadata.agentId !== `${environment}-${schema}` ||
        metadata.channelId !== CHANNEL
      )
        throw new CopilotActivityDetailError("identity");
      const history = await read(
        `/powervirtualagents/bots/${schema}/channels/unknown/conversations/${threadSegment}/history?api-version=1&pageSize=50&filterType=All`,
        "history",
      );
      check();
      if (
        !object(history) ||
        !Array.isArray(history.activities) ||
        history.activities.length > MAX_ACTIVITIES
      )
        throw new CopilotActivityDetailError("response");
      // Identity checks cover the entire returned page before any metadata projection.
      for (const item of history.activities) {
        if (
          !object(item) ||
          !object(item.conversation) ||
          typeof item.conversation.id !== "string" ||
          !ACTIVITY_GUID_PATTERN.test(item.conversation.id) ||
          item.conversation.id.toLowerCase() !== conversationId ||
          item.channelId !== CHANNEL
        )
          throw new CopilotActivityDetailError("identity");
      }
      const eventCounts = Object.fromEntries(EVENTS.map((name) => [name, 0])) as Record<
        string,
        number
      >;
      const messageCounts = { bot: 0, user: 0, unknownRole: 0 };
      let unknownEventCount = 0,
        unknownActivityTypeCount = 0;
      const activities = (history.activities as Record<string, unknown>[]).map((item, index) => {
        const time = timestamp(item.timestamp);
        const type = item.type === "message" || item.type === "event" ? item.type : "unknown";
        let role: "bot" | "user" | "unknown" | null = null;
        let eventName: string | null = null;
        if (type === "message") {
          role =
            object(item.from) && (item.from.role === "bot" || item.from.role === "user")
              ? item.from.role
              : "unknown";
          messageCounts[role === "unknown" ? "unknownRole" : role]++;
        } else if (type === "event") {
          if (typeof item.name === "string" && eventSet.has(item.name)) {
            eventName = item.name;
            eventCounts[eventName]!++;
          } else unknownEventCount++;
        } else unknownActivityTypeCount++;
        const value =
          eventName === "DynamicPlanStepBindUpdate" && object(item.value) ? item.value : null;
        return {
          index,
          type,
          timestamp: time,
          timestampState: time === null ? state(item.timestamp) : "available",
          role,
          eventName,
          ...(eventName === "DynamicPlanStepBindUpdate"
            ? {
                parameterMetadata: {
                  arguments: argumentKeys(value?.arguments),
                  autoFilledArguments: autoFilledNames(value?.autoFilledArguments),
                },
              }
            : {}),
        };
      });
      check();
      return {
        experimental: true,
        source: "studio-own-user-evaluation-history-metadata",
        environment,
        agentId,
        conversationId,
        channelId: CHANNEL,
        ownershipPreflight: "matched-first-50-projected-own-user-activity-rows",
        exactMetadataIdentityMatched: true,
        allHistoryRowsIdentityMatched: true,
        activities,
        returnedActivityCount: activities.length,
        messageCounts,
        eventCounts,
        unknownEventCount,
        unknownActivityTypeCount,
        requestedWirePageSize: 50,
        continuation: "not-returned-or-followed",
        totalCount: null,
        ...(parsed.data.includeToolCatalogStructure === true && parsed.data.toolCatalogTarget
          ? {
              toolCatalogStructure: projectCopilotMcpCatalogStructure(
                history.activities,
                parsed.data.toolCatalogTarget,
              ),
              ...(parsed.data.includeRecordedToolSchema === true
                ? {
                    recordedToolSchema: projectCopilotRecordedToolSchema(
                      history.activities,
                      parsed.data.toolCatalogTarget,
                    ),
                  }
                : {}),
            }
          : {}),
        ...(parsed.data.includeBotMessages === true
          ? { botMessages: projectBotMessages(history.activities as Record<string, unknown>[]) }
          : {}),
        ...(parsed.data.includeToolResults === true && parsed.data.toolResultTarget
          ? {
              toolResults: projectToolResults(
                history.activities as Record<string, unknown>[],
                parsed.data.toolResultTarget,
              ),
            }
          : {}),
        notice:
          parsed.data.includeToolResults === true
            ? `One first history page, not an exhaustive transcript. Selected recorded getJiraIssue results were explicitly requested and are bounded, target-correlated, and best-effort redacted; they are not a fresh read or proof of write actions. ${parsed.data.includeBotMessages === true ? "Bot-message text was separately requested and remains untrusted, not necessarily final or execution proof." : "Message text remains withheld."} Selected ticket fields may contain sensitive business information. Raw observations, user-message text fields, attachments, thought/debug content, planner internals, account/thread metadata and continuation tokens are not projected. Existing ownership checks, evaluation-only transport and no-fallback behavior are unchanged.`
            : parsed.data.includeBotMessages === true
              ? "One first history page, not an exhaustive transcript. Bot-message text was explicitly requested and is bounded, conservatively redacted and untrusted; it is not necessarily a final answer or proof of tool success and may contain identifiers or sensitive personal/business information. User-message text fields, attachments, thought/debug event content, event values, parameter values, account/thread metadata and continuation tokens are not projected. The first-50 ownership gate and evaluation-channel-only transport are unchanged; no alternate route or authentication fallback is attempted."
              : "Metadata only from one first history page, not an exhaustive transcript. Event names and recorded bind-event parameter-key presence do not prove executed tool inputs, authorization, returned ticket data or successful actions. Only closed known parameter names are exposed; unknown names are counted. No message text, thought/debug content, event values, parameter values, user IDs, opaque thread IDs, URLs or continuation tokens are returned. The first-50 ownership gate and evaluation-channel-only transport are deliberate experimental limits; no alternate route or authentication fallback is attempted.",
      };
    };
    try {
      return await Promise.race([
        work(),
        new Promise<never>((_, reject) => {
          timer = setTimeout(() => {
            controller.abort();
            destroy();
            reject(new CopilotActivityDetailError("deadline"));
          }, DEADLINE_MS);
        }),
      ]);
    } catch (error) {
      if (error instanceof CopilotActivityDetailError) throw error;
      throw new CopilotActivityDetailError("response");
    } finally {
      clearTimeout(timer);
      controller.abort();
      destroy();
    }
  }
}
